// client/src/components/pages/LexiStar/Messages/Conversation.jsx
//
// One driver's conversation (FR-DRV-005, FR-NTF-010): everything sent to and
// received from the driver on every channel, oldest first, as chat bubbles —
// outbound on the right with their delivery state, inbound on the left.

import React, { useEffect, useRef } from 'react';
import { formatMiningDateLong, formatMiningTime } from '../../../../utils/dateUtils';
import { StateChip, ChannelBadge } from './MessageAtoms';
import { buttonsOf, intentLabel, fmtZar, messageCost } from './messageMeta';

const dayKey = (iso) => formatMiningDateLong(iso);

export default function Conversation({ messages, driverName, onOpenMessage }) {
  const endRef = useRef(null);
  const sorted = [...messages].sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [messages]);

  if (!sorted.length) {
    return (
      <p className="p-6 text-sm text-slate-500">
        No messages with {driverName || 'this driver'} in this window.
      </p>
    );
  }

  let lastDay = null;
  return (
    <ol
      className="p-4 space-y-2 bg-slate-100/60"
      aria-label={`Conversation with ${driverName || 'driver'}`}
    >
      {sorted.map((m) => {
        const day = dayKey(m.created_at);
        const showDay = day !== lastDay;
        lastDay = day;
        const inbound = m.direction === 'in';
        const cost = messageCost(m);
        return (
          <React.Fragment key={m.id}>
            {showDay && (
              <li className="text-center py-1" aria-hidden="true">
                <span className="px-2 py-0.5 rounded-full bg-surface text-[11px] text-slate-500 shadow-sm">
                  {day}
                </span>
              </li>
            )}
            <li
              className={`flex ${inbound ? 'justify-start' : 'justify-end'}`}
              aria-label={`${inbound ? 'From' : 'To'} the driver at ${formatMiningTime(m.created_at)}`}
            >
              <button
                type="button"
                onClick={() => onOpenMessage?.(m)}
                className={`max-w-[80%] text-left rounded-2xl px-3 py-2 shadow-sm ${inbound ? 'bg-surface rounded-tl-sm' : 'bg-primary-50 rounded-tr-sm ring-1 ring-primary-100'}`}
              >
                <span className="flex items-center gap-2 text-[11px] text-slate-500 mb-0.5">
                  <ChannelBadge channel={m.channel} />
                  {!inbound && <span>{intentLabel(m)}</span>}
                </span>
                <span className="block whitespace-pre-wrap text-sm text-slate-900">
                  {m.body || <em className="text-slate-400">(no text)</em>}
                </span>
                {!inbound && buttonsOf(m).length > 0 && (
                  <span className="mt-1.5 flex flex-wrap gap-1">
                    {buttonsOf(m).map((b) => (
                      <span
                        key={b.id}
                        className="px-2 py-0.5 rounded-full bg-surface text-[11px] font-semibold text-primary-700 ring-1 ring-slate-200"
                      >
                        {b.label}
                      </span>
                    ))}
                  </span>
                )}
                <span className="mt-1 flex items-center justify-end gap-2 text-[10px] text-slate-500 tabular-nums">
                  {formatMiningTime(m.created_at)}
                  {!inbound && <StateChip state={m.state} />}
                  {cost !== null && cost > 0 && <span>{fmtZar(cost)}</span>}
                </span>
              </button>
            </li>
          </React.Fragment>
        );
      })}
      <li ref={endRef} aria-hidden="true" />
    </ol>
  );
}
